import {
  type AcademyPayment as AcademyPaymentData,
  paymentApi,
  type PaymentMessage,
  type PaymentMessageParams,
  type PaymentStudent as PaymentStudentData,
} from '@mathflat/domain/Payment'
import { Sms, SmsCollection } from 'src/dto'
import { AcademyPayment, StudentPayment } from 'src/dto/Payment'
import ExtendedDate from 'src/models/ExtendedDate'
import ExtendedMap from 'src/models/ExtendedMap'

import { mpEventInfo } from '@/constant'
import { PaymentMessageStatus, PaymentStudent, PaymentStudentColl } from '@/dto/Payment'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

type DateParams = {
  year: string
  month: string
}

const currentDate = new Date()
class PaymentController implements Controller {
  adapter = {
    payment: makeAdapter({
      from(data: AcademyPaymentData): AcademyPayment {
        return new AcademyPayment(data)
      },
    }),
    student: makeAdapter({
      from(data: PaymentStudentData[]) {
        const students = data.map((student) => new PaymentStudent(student))
        const studentColl = new PaymentStudentColl(ExtendedMap.fromArr(students, 'studentId'))

        return new StudentPayment(studentColl)
      },
    }),
    message: makeAdapter({
      from({ messages: { content }, ...rest }: PaymentMessage): PaymentMessageStatus {
        const smsArr = content.map((message) => new Sms(message))
        const messageColl = new SmsCollection(ExtendedMap.fromArr(smsArr, 'id'))
        return new PaymentMessageStatus({ messageColl, ...rest })
      },
    }),
  }

  async fetchAcademyPayment(
    yearMonth: DateParams | undefined = {
      year: currentDate.getFullYear().toString(),
      month: ExtendedDate.setPrefixMonth(currentDate.getMonth() + 1),
    },
  ) {
    const data = await paymentApi.getAcademyPayment(this.makeYearDashMonth(yearMonth), {
      mp_event_name: mpEventInfo.학원과금정보조회,
    })
    return this.adapter.payment.from(data)
  }

  async fetchAcademyStudentPayments(dateParams: DateParams) {
    const data = await paymentApi.getPaymentStudents(this.makeYearDashMonth(dateParams), {
      mp_event_name: mpEventInfo.추가요금조회,
    })
    return this.adapter.student.from(data)
  }

  async fetchPaymentMessage(
    pageParams: Omit<PaymentMessageParams, 'yearMonth'>,
    dateParams: DateParams,
  ) {
    const data = await paymentApi.getPaymentMessage(
      { ...pageParams, yearMonth: this.makeYearDashMonth(dateParams) },
      { mp_event_name: mpEventInfo.문자과금조회 },
    )
    return { paymentMessageStatus: this.adapter.message.from(data), last: data.messages.last }
  }

  private makeYearDashMonth({ year, month }: DateParams) {
    return `${year}-${month}`
  }
}
export const paymentController = new PaymentController()
